"use client";

import React from "react";
import { ColorModeContext, useColorMode, UseColorModeOptions } from "./ColorModeContext";
import { ApplyTheme } from "./ApplyTheme";
import { ApplyFontFamily } from "./ApplyFontFamily";

import "../../styles/reset.css";
import { UIOptionsContext } from "./OptionsContext";

interface YoungProviderOptions {
  colorMode: UseColorModeOptions;
  /**
   * Whether to apply the font family to the body.
   *
   * @default false
   */
  shouldApplyFontFamily: boolean;

  /**
   * Whether to make typography components responsive.
   *
   * @default false
   */
  shouldUseResponsiveTypography: boolean;
}

export interface YoungProviderProps {
  children: React.ReactNode;
  options?: Partial<YoungProviderOptions>;
}

/**
 * YoungProvider is the component that handles the theme context and applies the theme to the body.
 * It should be used at the root of your application.
 *
 * If you are using Next.js with server components, it is advised to implement your own version of
 * this Provider. To get started simply apply `lightThemeClass` or `darkThemeClass` to the HTML
 * body.
 */
export const YoungProvider = ({ children, options }: YoungProviderProps) => {
  const colorMode = useColorMode(options?.colorMode);

  const shouldApplyFontFamily = options?.shouldApplyFontFamily;
  const shouldUseResponsiveTypography = options?.shouldUseResponsiveTypography || false;

  const uiOptions = React.useMemo(() => ({
    shouldUseResponsiveTypography,
  }), [shouldUseResponsiveTypography]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <UIOptionsContext.Provider value={uiOptions}>
        <ApplyTheme />
        {shouldApplyFontFamily && <ApplyFontFamily />}
        {children}
      </UIOptionsContext.Provider>
    </ColorModeContext.Provider>
  );
};
